import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import {
  dashboard,
  operations,
  type DashboardSummary,
  type Operation,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDate, formatMoney, formatMoneyCompact } from '@/shared/lib/format';
import { InstallAppCard } from '@/shared/pwa/InstallAppCard';
import { Alert } from '@/shared/ui/Alert';
import { baixarCsv, moedaParaPlanilha } from '@/shared/lib/exportar';
import { Button } from '@/shared/ui/Button';
import { buttonClassName } from '@/shared/ui/buttonStyles';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { KpiCard } from '@/shared/ui/KpiCard';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Skeleton } from '@/shared/ui/Skeleton';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { statusLabel } from '@/shared/ui/statusLabels';

function saudacao(): string {
  const hora = new Date().getHours();
  if (hora < 12) return 'Bom dia';
  if (hora < 18) return 'Boa tarde';
  return 'Boa noite';
}

/** Ordem de apresentacao da esteira no painel, sem as etapas terminais. */
const etapasEmAndamento = [
  'aguardando_credito',
  'em_subscricao',
  'em_juridico',
  'aguardando_comercial',
  'minuta_gerada',
  'aguardando_ccg',
  'aguardando_assinatura',
  'em_emissao',
];

export function DashboardPage() {
  const { session, can } = useAuth();
  const podeVerPainel = can('dashboard.painel.view');

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['dashboard', 'summary'],
    queryFn: ({ signal }) => dashboard.summary(signal),
    enabled: podeVerPainel,
  });

  if (!session) return null;

  const primeiroNome = session.user.name.split(' ')[0] ?? session.user.name;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={`${saudacao()}, ${primeiroNome}.`}
        description={`${session.tenant.name} - visao geral da operacao.`}
        actions={
          <div className="flex items-center gap-2">
            {/* Os relatorios do OnPoint (P-BI-1) ficam em /bi; o Painel continua como a
                visao rapida da empresa. */}
            {can('bi.painel.view') ? (
              <Link to="/bi" className={buttonClassName({ variant: 'secondary' })}>
                Relatorios
              </Link>
            ) : null}
            {podeVerPainel ? (
              <Button
                variant="secondary"
                disabled={!data}
                onClick={() => data && exportarPainel(data)}
              >
                Exportar
              </Button>
            ) : null}
          </div>
        }
      />

      {!podeVerPainel ? (
        <Alert tone="info" title="Painel indisponivel para o seu acesso">
          O seu perfil nao inclui a visualizacao do painel executivo. Use o menu lateral
          para acessar os modulos disponiveis.
        </Alert>
      ) : null}

      {isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar os indicadores">
          <div className="flex flex-col gap-3">
            <p>{error.message}</p>
            <Button size="sm" variant="secondary" onClick={() => void refetch()}>
              Tentar de novo
            </Button>
          </div>
        </Alert>
      ) : null}

      {podeVerPainel && !isError ? (
        <>
          <Indicadores data={data} loading={isPending} />

          <div className="grid gap-4 lg:grid-cols-2">
            <EvolucaoMensal data={data} loading={isPending} />
            <DistribuicaoPorModalidade data={data} loading={isPending} />
          </div>

          <OperacoesRecentes />

          <div className="grid gap-4 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <EsteiraPorEtapa data={data} loading={isPending} />
            </div>
            <div className="flex flex-col gap-4">
              <Relacionamento data={data} loading={isPending} />
              <InstallAppCard
                appName={session.tenant.branding.pwa_name || 'SeguraCore'}
              />
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

function Indicadores({
  data,
  loading,
}: {
  data?: DashboardSummary | undefined;
  loading: boolean;
}) {
  const totais = data?.operations;
  const derivados = data?.derived;

  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <KpiCard
        icon="clock"
        label="Operacoes em andamento"
        value={String(totais?.open ?? 0)}
        hint={
          totais
            ? `${formatMoneyCompact(totais.insured_amount_open)} garantidos`
            : undefined
        }
        loading={loading}
      />
      <KpiCard
        icon="alert"
        tone="danger"
        highlight={totais !== undefined && totais.overdue > 0}
        label="Prazo vencido"
        value={String(totais?.overdue ?? 0)}
        hint="Etapas fora do prazo acordado"
        loading={loading}
      />
      <KpiCard
        icon="shield"
        tone="success"
        label="Emitidas"
        value={String(totais?.issued ?? 0)}
        hint={totais ? `${formatMoney(totais.premium_issued)} em premio` : undefined}
        loading={loading}
      />
      <KpiCard
        icon="close"
        tone="danger"
        label="Declinadas"
        value={String(totais?.declined ?? 0)}
        hint="Encerradas sem emissao"
        loading={loading}
      />
      <KpiCard
        icon="clock"
        tone="warning"
        label="Aguardando credito"
        value={String(data?.operations_by_stage['aguardando_credito'] ?? 0)}
        hint="Primeira etapa da esteira"
        loading={loading}
      />
      <KpiCard
        icon="coins"
        tone="violet"
        label="Comissoes do mes"
        value={
          derivados ? formatMoneyCompact(derivados.commissions_this_month) : 'R$ 0'
        }
        hint="Competencia corrente, sem as canceladas"
        loading={loading}
      />
      <KpiCard
        icon="percent"
        tone="cyan"
        label="Conversao"
        value={derivados ? `${(derivados.conversion_bps / 100).toFixed(1)}%` : '0,0%'}
        hint="Das operacoes decididas, quantas viraram apolice"
        loading={loading}
      />
      <KpiCard
        icon="target"
        tone="indigo"
        label="Ticket medio"
        value={derivados ? formatMoneyCompact(derivados.average_ticket) : 'R$ 0'}
        hint="Premio medio por apolice emitida"
        loading={loading}
      />
      <KpiCard
        icon="alert"
        tone="warning"
        highlight={derivados !== undefined && derivados.expiring_in_30_days > 0}
        label="Vencendo em 30 dias"
        value={String(derivados?.expiring_in_30_days ?? 0)}
        hint="Apolices vigentes que expiram no proximo mes"
        loading={loading}
      />
    </div>
  );
}

function EsteiraPorEtapa({
  data,
  loading,
}: {
  data?: DashboardSummary | undefined;
  loading: boolean;
}) {
  const porEtapa = data?.operations_by_stage ?? {};
  const emAndamento = etapasEmAndamento.map((etapa) => ({
    etapa,
    total: porEtapa[etapa] ?? 0,
  }));
  const maior = Math.max(1, ...emAndamento.map((item) => item.total));
  const temMovimento = emAndamento.some((item) => item.total > 0);

  return (
    <Card className="h-full">
      <CardHeader
        title="Esteira de operacoes"
        description="Onde as operacoes em andamento estao paradas agora."
        action={
          <Link
            to="/operacoes"
            className="text-xs font-medium text-brand-600 hover:text-brand-700"
          >
            Ver todas
          </Link>
        }
      />
      <CardBody>
        {loading ? (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 6 }, (_, index) => (
              <Skeleton key={index} className="h-8 w-full" />
            ))}
          </div>
        ) : !temMovimento ? (
          <EmptyState
            icon="clock"
            title="Nenhuma operacao em andamento"
            description="Quando a primeira operacao for aberta, a distribuicao por etapa aparece aqui."
            action={
              <Link
                to="/operacoes"
                className={buttonClassName({ variant: 'secondary' })}
              >
                Ir para operacoes
              </Link>
            }
          />
        ) : (
          <ul className="flex flex-col gap-2.5">
            {emAndamento.map(({ etapa, total }) => (
              <li key={etapa}>
                {/* A barra leva a lista ja filtrada por aquela etapa. Ver o numero e
                    depois ter de refazer o filtro na mao e o passo que o painel
                    existe para poupar. */}
                <Link
                  to={`/operacoes?stage=${etapa}`}
                  aria-label={`${total} em ${statusLabel('operacao', etapa)}`}
                  className="flex items-center gap-3 rounded-lg px-1.5 py-1 transition-colors duration-(--duration-fast) hover:bg-brand-50"
                >
                  <span className="w-44 shrink-0 truncate text-xs text-ink-600">
                    {statusLabel('operacao', etapa)}
                  </span>
                  <span className="h-2 flex-1 overflow-hidden rounded-full bg-ink-100">
                    <span
                      className="block h-full rounded-full bg-brand-500 bg-linear-to-r from-brand-500 to-accent-500 transition-[width] duration-(--duration-slow)"
                      style={{ width: `${(total / maior) * 100}%` }}
                    />
                  </span>
                  <span className="w-8 shrink-0 text-right text-xs font-semibold tabular-nums text-ink-800">
                    {total}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function Relacionamento({
  data,
  loading,
}: {
  data?: DashboardSummary | undefined;
  loading: boolean;
}) {
  const corretores = data?.brokers_by_stage ?? {};
  const tomadores = data?.policyholders_by_status ?? {};

  const linhas = [
    { label: 'Corretores ativos', valor: corretores.ativo ?? 0, para: '/corretores' },
    {
      label: 'Corretores em ativacao',
      valor:
        (corretores.novo ?? 0) +
        (corretores.em_documentacao ?? 0) +
        (corretores.em_validacao ?? 0),
      para: '/corretores',
    },
    { label: 'Tomadores ativos', valor: tomadores.ativo ?? 0, para: '/tomadores' },
  ];

  return (
    <Card>
      <CardHeader title="Relacionamento" description="Base de parceiros e clientes." />
      <CardBody className="flex flex-col gap-3 p-4">
        {linhas.map((linha) => (
          <Link
            key={linha.label}
            to={linha.para}
            className="flex items-center justify-between rounded-lg px-1.5 py-1.5 text-sm transition-colors duration-(--duration-fast) hover:bg-brand-50"
          >
            <span className="text-ink-600">{linha.label}</span>
            {loading ? (
              <Skeleton className="h-4 w-8" />
            ) : (
              <span className="rounded-full bg-brand-50 px-2 py-0.5 font-semibold tabular-nums text-brand-700 ring-1 ring-brand-100 ring-inset">
                {linha.valor}
              </span>
            )}
          </Link>
        ))}
      </CardBody>
    </Card>
  );
}

/**
 * Evolucao do premio emitido, mes a mes.
 *
 * A serie vem completa do servidor, com zero nos meses sem emissao. Um grafico que
 * pula o mes vazio comprime o tempo e faz uma queda parecer estabilidade.
 */
function EvolucaoMensal({
  data,
  loading,
}: {
  data?: DashboardSummary | undefined;
  loading: boolean;
}) {
  const serie = data?.monthly_series ?? [];
  const maior = Math.max(1, ...serie.map((ponto) => ponto.premium.amount));
  const temMovimento = serie.some((ponto) => ponto.count > 0);

  return (
    <Card className="h-full">
      <CardHeader
        title="Evolucao mensal"
        description="Premio emitido nos ultimos doze meses."
      />
      <CardBody>
        {loading ? (
          <Skeleton className="h-40 w-full" />
        ) : !temMovimento ? (
          <EmptyState
            icon="clock"
            title="Nenhuma apolice emitida ainda"
            description="A serie aparece a partir da primeira emissao."
          />
        ) : (
          <div
            className="flex h-40 items-end gap-1.5"
            role="img"
            aria-label="Premio emitido por mes"
          >
            {serie.map((ponto) => (
              <div
                key={ponto.competence}
                className="flex flex-1 flex-col items-center gap-1"
              >
                <span
                  title={`${ponto.competence}: ${formatMoney(ponto.premium)} em ${ponto.count} apolice(s)`}
                  className="w-full rounded-t-md bg-brand-500 bg-linear-to-t from-brand-500 to-accent-400 transition-[height] duration-(--duration-slow)"
                  style={{
                    height: `${Math.max(2, (ponto.premium.amount / maior) * 100)}%`,
                  }}
                />
                <span className="text-[0.5625rem] text-ink-400">
                  {ponto.competence.slice(5)}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/** Participacao de cada modalidade na carteira emitida. */
function DistribuicaoPorModalidade({
  data,
  loading,
}: {
  data?: DashboardSummary | undefined;
  loading: boolean;
}) {
  const fatias = data?.modality_breakdown ?? [];
  const total = fatias.reduce((soma, fatia) => soma + fatia.premium.amount, 0);

  return (
    <Card className="h-full">
      <CardHeader
        title="Distribuicao por modalidade"
        description="Onde o premio da carteira esta concentrado."
      />
      <CardBody>
        {loading ? (
          <Skeleton className="h-40 w-full" />
        ) : fatias.length === 0 ? (
          <EmptyState
            icon="layers"
            title="Nenhuma modalidade emitida"
            description="A distribuicao aparece a partir da primeira emissao."
          />
        ) : (
          <ul className="flex flex-col gap-2.5">
            {fatias.slice(0, 6).map((fatia) => (
              <li key={fatia.modality_id} className="flex items-center gap-3">
                <span className="w-40 shrink-0 truncate text-xs text-ink-600">
                  {fatia.label || 'Modalidade removida'}
                </span>
                <span className="h-2 flex-1 overflow-hidden rounded-full bg-ink-100">
                  <span
                    className="block h-full rounded-full bg-accent-500 bg-linear-to-r from-accent-600 to-accent-400"
                    style={{
                      width: `${total > 0 ? (fatia.premium.amount / total) * 100 : 0}%`,
                    }}
                  />
                </span>
                <span className="w-20 shrink-0 text-right text-2xs tabular-nums text-ink-700">
                  {formatMoneyCompact(fatia.premium)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

/**
 * Exporta o quadro do painel.
 *
 * Sai um indicador por linha, e nao uma linha unica com muitas colunas: quem recebe
 * o arquivo costuma coloca-lo ao lado do mes anterior, e a comparacao fica mais
 * simples com o indicador na primeira coluna.
 */
function exportarPainel(data: DashboardSummary): void {
  const linhas: { indicador: string; valor: string }[] = [
    { indicador: 'Operacoes em andamento', valor: String(data.operations.open) },
    { indicador: 'Prazo vencido', valor: String(data.operations.overdue) },
    { indicador: 'Emitidas', valor: String(data.operations.issued) },
    { indicador: 'Declinadas', valor: String(data.operations.declined) },
    {
      indicador: 'Valor garantido em andamento',
      valor: moedaParaPlanilha(data.operations.insured_amount_open),
    },
    {
      indicador: 'Premio emitido',
      valor: moedaParaPlanilha(data.operations.premium_issued),
    },
    {
      indicador: 'Comissoes do mes',
      valor: moedaParaPlanilha(data.derived.commissions_this_month),
    },
    {
      indicador: 'Conversao (%)',
      valor: (data.derived.conversion_bps / 100).toFixed(2).replace('.', ','),
    },
    {
      indicador: 'Ticket medio',
      valor: moedaParaPlanilha(data.derived.average_ticket),
    },
    {
      indicador: 'Vencendo em 30 dias',
      valor: String(data.derived.expiring_in_30_days),
    },
    ...Object.entries(data.operations_by_stage).map(([etapa, total]) => ({
      indicador: `Etapa: ${statusLabel('operacao', etapa)}`,
      valor: String(total),
    })),
    ...data.monthly_series.map((ponto) => ({
      indicador: `Premio em ${ponto.competence}`,
      valor: moedaParaPlanilha(ponto.premium),
    })),
  ];

  baixarCsv('painel', linhas, [
    { titulo: 'Indicador', valor: (linha) => linha.indicador },
    { titulo: 'Valor', valor: (linha) => linha.valor },
  ]);
}

/**
 * As ultimas operacoes abertas.
 *
 * O painel responde quanto e quantos; esta tabela responde quais. Quem abre a
 * plataforma no inicio do dia costuma querer as duas coisas, e ter de sair para a
 * lista para ver os nomes e um passo que o painel pode poupar.
 *
 * A consulta e a mesma da tela de operacoes, com o recorte pequeno: o painel nao
 * pagina, ele mostra as primeiras e leva para a lista.
 */
function OperacoesRecentes() {
  const consulta = useQuery({
    queryKey: ['operations', 'recentes'],
    queryFn: ({ signal }) => operations.list({ limit: 8 }, signal),
  });

  const colunas: Column<Operation>[] = [
    {
      key: 'tomador',
      header: 'Tomador',
      render: (operacao) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-ink-900">
            {operacao.policyholder.label}
          </p>
          <p className="truncate text-2xs text-ink-500">{operacao.subject}</p>
        </div>
      ),
    },
    {
      // A coluna mostra a modalidade, e por isso tem o nome dela. Antes o titulo era
      // "Produto" sobre o nome da modalidade (defeito 14 de "Operacao" do inventario 47,
      // anexo C).
      key: 'modalidade',
      header: 'Modalidade',
      hideBelow: 'md',
      render: (operacao) => operacao.modality.label,
    },
    {
      key: 'valor',
      header: 'Valor garantido',
      align: 'right',
      render: (operacao) => formatMoney(operacao.insured_amount),
    },
    {
      key: 'etapa',
      header: 'Etapa',
      render: (operacao) => <StatusBadge kind="operacao" value={operacao.stage} />,
    },
    {
      key: 'responsavel',
      header: 'Responsavel',
      hideBelow: 'lg',
      render: (operacao) =>
        operacao.owner_label || <span className="text-ink-400">sem responsavel</span>,
    },
    {
      key: 'criado',
      header: 'Aberta em',
      align: 'right',
      hideBelow: 'lg',
      render: (operacao) => formatDate(operacao.created_at),
    },
  ];

  return (
    <Card>
      <CardHeader
        title="Operacoes recentes"
        description="As ultimas abertas, com quem responde por cada uma."
        action={
          <Link
            to="/operacoes"
            className="text-xs font-medium text-brand-600 hover:text-brand-700"
          >
            Ver todas
          </Link>
        }
      />
      <DataTable
        columns={colunas}
        items={consulta.data?.items ?? []}
        keyOf={(operacao) => operacao.id}
        loading={consulta.isPending}
        empty={
          <EmptyState
            icon="clock"
            title="Nenhuma operacao aberta"
            description="A primeira operacao aparece aqui assim que for aberta."
          />
        }
      />
    </Card>
  );
}
